import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { Save, RotateCcw } from "lucide-react";

/**
 * fields = [{ key, label, type: 'text'|'textarea'|'image'|'number', placeholder, rows }]
 */
export default function SectionEditor({ title, sectionKey, defaults, fields, onSaved }) {
  const [form, setForm] = useState(defaults);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/site-content/${sectionKey}`);
        if (!alive) return;
        setForm({ ...defaults, ...(data.data || {}) });
      } catch { /* keep defaults */ }
      setLoaded(true);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/site-content/${sectionKey}`, { data: form });
      toast.success(`${title} saved.`);
      if (onSaved) onSaved(form);
    } catch { toast.error("Save failed."); }
    setSaving(false);
  };

  const reset = async () => {
    if (!confirm("Reset to defaults? Any customization for this section will be cleared.")) return;
    setSaving(true);
    try {
      await api.post(`/site-content/${sectionKey}`, { data: {} });
      setForm(defaults);
      toast.success("Reset to defaults.");
    } catch { toast.error("Reset failed."); }
    setSaving(false);
  };

  if (!loaded) return <div className="card-light p-6 text-neutral-400">Loading…</div>;

  return (
    <div className="card-light p-6 space-y-5" data-testid={`section-editor-${sectionKey}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-tag" style={{ color: "#000" }}>{sectionKey}</div>
          <div className="font-heading uppercase font-bold text-xl">{title}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} disabled={saving} className="btn-ghost-dark !py-2 !px-4 text-sm" data-testid={`reset-${sectionKey}`}><RotateCcw size={14}/>Reset</button>
          <button onClick={save} disabled={saving} className="btn-gold !py-2 !px-4 text-sm" data-testid={`save-${sectionKey}`}><Save size={14}/>{saving?"Saving…":"Save"}</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
            {f.type === "image" ? (
              <ImageUpload label={f.label} value={form[f.key]} onChange={(v)=>set(f.key, v)} testid={`field-${sectionKey}-${f.key}`}/>
            ) : (
              <>
                <label className="label-tag" style={{ color: "#000" }}>{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea value={form[f.key] ?? ""} onChange={(e)=>set(f.key, e.target.value)} rows={f.rows || 3}
                    placeholder={f.placeholder}
                    className="mt-2 w-full border border-black/15 bg-white p-3 focus:border-[color:var(--gold)] outline-none text-sm"
                    data-testid={`field-${sectionKey}-${f.key}`}/>
                ) : (
                  <input type={f.type === "number" ? "number" : "text"} value={form[f.key] ?? ""}
                    placeholder={f.placeholder}
                    onChange={(e)=>set(f.key, e.target.value)} className="input-sharp mt-2" data-testid={`field-${sectionKey}-${f.key}`}/>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
